// =============================================================================
// Books (§7 Catalogue). Desktop screen: a searchable table, a side panel for
// detail, and Add Book.
//
// A single-copy title is one row. A title with several copies gets a heading
// row for the title, then ONE ROW PER COPY beneath it — a two-copy title showing
// only the borrower of the copy due back first hid the second copy entirely,
// which reads as missing data rather than as a summary.
// =============================================================================

import { el, mount, formatDate } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { listCatalogue, titleCopies, copiesForTitles, updateTitle, listCategories,
         titleLoanHistory, updateCopyCondition } from '../lib/api.js';
import { addBookPanel } from './add-books.js';
import { liveSearch } from '../lib/live-search.js';
import { spinner, skeletonRows, skeletonBlock } from '../lib/loading.js';
import { flashNotice, flashSaved } from '../lib/notice.js';

export function booksScreen(root) {
  let rows = [];
  let search = '';
  let loading = true;
  let selected = null;      // title_id whose panel is open
  let copies = [];
  // copy rows for every multi-copy title in the table, keyed by title_id
  let expanded = new Map();
  let panelLoading = false;
  let adding = false;
  let editing = false;         // the open title is being corrected
  let categories = [];
  let savingEdit = false;
  // Blocks a second click while the label sheet is being gathered.
  let printing = false;
  // Client-side sort. The catalogue is already fully in memory (listCatalogue
  // fetches up to 500), so sorting here costs one array pass and no round trip.
  // Note the 500 ceiling: past that, sorting would silently cover only the
  // first page and this has to move to the database.
  // Titles ticked for a partial label run, by title_id. Kept as ids rather than
  // indexes so sorting and re-searching cannot silently reassign the selection
  // to different books.
  let picked = new Set();
  // Filters run in the browser: the catalogue is already loaded, so narrowing
  // it is an array pass rather than a round trip, and it stays instant on a
  // connection where a round trip is not.
  let availabilityFilter = 'all';
  let categoryFilter = '';
  let filtersOpen = false;      // phones only — see .filters-toggle
  let sortKey = 'title';
  let sortDir = 1;              // 1 ascending, -1 descending
  // Borrowing history for the open title. Loaded alongside the copies, not on a
  // second click: "who has had this book" is the question people open the panel
  // to answer, and a second spinner to get it reads as the app being slow.
  let history = [];
  let historyFailed = false;
  // copy_id currently being annotated, and whether its save is in flight.
  let editingNote = null;
  let savingNote = false;
  // Held across the render() that closes the editor, so the confirmation is not
  // mounted into a node that is about to be replaced.
  let pendingNoteToast = null;
  // Built once and REUSED across renders. Rebuilding it would discard the
  // session list of everything added so far — which is the print queue — every
  // time a save refreshed the table underneath.
  let addPanelNode = null;

  const view = el('div', { class: 'screen' });
  mount(root, view);

  const input = el('input', {
    type: 'search', class: 'text-input', value: search,
    placeholder: T.searchBooks, autocomplete: 'off', enterkeyhint: 'search',
    'aria-label': T.searchBooks,
  });

  // Typing searches — same reason as Members (lib/live-search.js). The
  // catalogue search is a round trip, so the debounce is a little longer.
  const live = liveSearch(input, async (term, stillCurrent) => {
    search = term;
    await load(stillCurrent);
  }, { delay: 300 });

  async function load(stillCurrent = () => true) {
    loading = true; render();
    try {
      const fetched = await listCatalogue({ search });
      // A slow request for an earlier search must not overwrite a later one.
      if (!stillCurrent()) return;
      rows = fetched;
      expanded = await loadExpansions(rows);
      // A search that no longer contains the open book must close its panel.
      // Otherwise the panel sits there forever waiting for a title that is not
      // in `rows` — previously a spinner that never resolved, and with the
      // skeleton it is an empty white column, which is worse.
      if (selected && !rows.some((r) => r.title_id === selected)) {
        selected = null; copies = []; history = []; editing = false; editingNote = null;
      }
      // Ticks are deliberately NOT pruned here. Printing already intersects
      // the selection with what is on screen (pickedVisible), so a tick for a
      // book the current search excludes cannot print — and silently deleting
      // it would throw away something the reader chose, which is worse than
      // remembering it if they search back.
    } catch (err) {
      if (!stillCurrent()) return;
      mount(view, el('p', { class: 'error' }, err.message));
      loading = false; return;
    }
    loading = false; render();
  }

  async function openPanel(titleId) {
    selected = titleId; adding = false; editing = false; panelLoading = true; render();
    history = []; historyFailed = false; editingNote = null;
    try {
      // In parallel: two sequential awaits made opening a book feel twice as
      // slow as it needed to on a Myanmar connection.
      const [cs, hist] = await Promise.all([
        titleCopies(titleId),
        titleLoanHistory(titleId).catch(() => { historyFailed = true; return []; }),
      ]);
      copies = cs;
      history = hist;
      if (!categories.length) categories = await listCategories().catch(() => []);
    } catch (err) {
      copies = [];
      panelLoading = false; render();
      return;
    }
    panelLoading = false; render();
  }

  function closePanel() {
    selected = null; adding = false; editing = false; copies = []; history = [];
    editingNote = null;
    // Drop the panel so the next "Add book" starts a fresh session rather than
    // showing a stale print queue from an earlier sitting.
    addPanelNode = null;
    render();
  }

  function render() {
    mount(view,
      el('div', { class: 'screen-head' },
        el('h1', { class: 'screen-title' }, T.booksTitle),
        visible().length
          ? el('button', { class: 'btn', onClick: printLabels, disabled: printing || null },
              printing ? T.preparingLabels
                : pickedVisible().length ? T.printSelected(pickedVisible().length)
                : filtersActive() ? T.printFiltered(visible().length)
                : T.printAllLabels)
          : null,
        el('button', {
          class: 'btn btn-primary',
          onClick: () => { adding = true; selected = null; render(); },
        }, T.addBook),
      ),

      el('form', {
        class: 'search-row',
        onSubmit: (e) => { e.preventDefault(); live.now(); },
      }, input, el('button', { class: 'btn', type: 'submit' }, T.search)),

      filterRow(),

      loading
        // Skeleton rather than a spinner: the catalogue is a table, and holding
        // its shape stops the page collapsing and jumping on every search.
        ? el('div', { role: 'status', 'aria-live': 'polite', 'aria-label': T.loading },
            skeletonRows(6, 5))
        : visible().length === 0
          ? el('p', { class: 'empty' },
              filtersActive() ? T.noBooksMatchFilter
                : search ? T.noBooksMatch : T.noBooksYet)
          : table(),

      (selected || adding) ? panel() : null,
    );
  }

  // The whole visible catalogue, one sheet. This is the bulk labelling run —
  // and it respects the current search, so a shelf can be done at a time.
  //
  // ONE request, not one per title. This previously awaited titleCopies() inside
  // a loop, so a 109-book catalogue meant 109 sequential round trips: roughly
  // twenty seconds of staring at an unchanged screen on a Myanmar connection,
  // with nothing to indicate it was working. copiesForTitles() already existed
  // and is what loadExpansions() below uses — this was simply never updated.
  // On a phone the table is already tight, so two dropdowns sitting above it
  // permanently would push the catalogue off the screen. The toggle is hidden
  // on desktop by CSS, where the row is simply always shown.
  function filterRow() {
    const toggle = el('button', {
      class: 'btn btn-sm filters-toggle', type: 'button',
      'aria-expanded': String(filtersOpen),
      onClick: () => { filtersOpen = !filtersOpen; render(); },
    }, filtersActive() ? T.filterOn : T.filter);

    const avail = el('select', { class: 'text-input', 'aria-label': T.availability,
      onChange: (e) => { availabilityFilter = e.target.value; render(); } },
      ...Object.keys(AVAILABILITY).map((k) =>
        el('option', { value: k, selected: k === availabilityFilter }, T.availabilityOption[k])));

    // Categories come from the same list Add Book uses. A category still in use
    // on a title but missing from the list would otherwise be unfilterable.
    const names = categories.map((c) => c.name);
    for (const r of rows) if (r.category && !names.includes(r.category)) names.push(r.category);
    names.sort((a, b) => a.localeCompare(b));

    const cat = el('select', { class: 'text-input', 'aria-label': T.category,
      onChange: (e) => { categoryFilter = e.target.value; render(); } },
      el('option', { value: '' }, T.allCategories),
      ...names.map((n) => el('option', { value: n, selected: n === categoryFilter }, n)));

    return el('div', { class: 'filters-block' },
      toggle,
      el('div', { class: `filters ${filtersOpen ? '' : 'filters-collapsed'}` },
        avail,
        cat,
        filtersActive()
          ? el('button', { class: 'btn btn-sm', type: 'button',
              onClick: () => { availabilityFilter = 'all'; categoryFilter = ''; render(); } },
              T.clearFilters)
          : null,
        el('span', { class: 'muted filters-count' }, T.showingCount(visible().length, rows.length)),
      ));
  }

  // What availability each option means. A multi-copy title can legitimately
  // match several at once — two out, one on the shelf — and that is not a bug
  // to be tidied away: the desk wants it in both answers.
  const AVAILABILITY = {
    all:       () => true,
    onShelf:   (r) => r.available_copies > 0,
    onLoan:    (r) => r.on_loan_copies > 0,
    overdue:   (r) => r.overdue_copies > 0,
    outOfService: (r) => r.out_of_service_copies > 0,
  };

  // Everything the reader can currently see. Sorting, printing, select-all and
  // the empty message all read from this, so a filtered "print all" cannot
  // quietly include books that are not on screen.
  function visible() {
    const byAvailability = AVAILABILITY[availabilityFilter] || AVAILABILITY.all;
    return rows.filter((r) =>
      byAvailability(r) && (!categoryFilter || r.category === categoryFilter));
  }

  const filtersActive = () => availabilityFilter !== 'all' || Boolean(categoryFilter);

  // Ticked titles that are actually on screen. A selection made before a search
  // must not quietly print books the reader can no longer see.
  function pickedVisible() {
    return visible().filter((r) => picked.has(r.title_id));
  }

  // Selected titles if any are ticked, otherwise everything the search shows.
  async function printLabels() {
    if (printing) return;
    const chosen = pickedVisible();
    const ids = (chosen.length ? chosen : visible()).map((r) => r.title_id);
    if (!ids.length) return;

    printing = true; render();
    let all = [];
    try {
      all = (await copiesForTitles(ids)).map((c) => ({ code: c.copy_code, title: c.title }));
    } catch (err) {
      printing = false; render();
      flashNotice(view, err.message);
      return;
    }
    printing = false; render();

    if (!all.length) return;
    const { openLabelSheet } = await import('../lib/labels.js');
    openLabelSheet(all);
  }

  // What each sortable column sorts BY. Kept separate from what the cell
  // displays: "1 of 2 available" should sort by how many are on the shelf, not
  // alphabetically by the string "1 of 2".
  const SORTS = {
    title:     (r) => (r.title || '').toLowerCase(),
    author:    (r) => (r.author || '').toLowerCase(),
    category:  (r) => (r.category || '').toLowerCase(),
    available: (r) => r.available_copies,
    added:     (r) => r.added_at || null,
    borrower:  (r) => (r.first_borrower || '').toLowerCase(),
    // Nulls last in ascending order: a shelf full of books that are not out is
    // not the interesting end of a "when is this due back" sort.
    due:       (r) => r.soonest_due || null,
  };

  function sortedRows() {
    const pick = SORTS[sortKey] || SORTS.title;
    return [...visible()].sort((a, b) => {
      const x = pick(a), y = pick(b);
      if (x === null || x === undefined || x === '') return y === null || y === undefined || y === '' ? 0 : 1;
      if (y === null || y === undefined || y === '') return -1;
      if (x < y) return -1 * sortDir;
      if (x > y) return 1 * sortDir;
      // Stable tiebreak, so a sort by category does not shuffle titles around
      // on every re-render.
      return (a.title || '').localeCompare(b.title || '');
    });
  }

  function sortBy(key) {
    if (sortKey === key) sortDir = -sortDir;
    else { sortKey = key; sortDir = 1; }
    render();
  }

  function sortableTh(key, label) {
    const active = sortKey === key;
    return el('th', {
      class: `th-sort ${active ? 'th-sort-active' : ''}`,
      tabindex: '0',
      role: 'button',
      // Screen readers announce the current sort rather than just "Title".
      'aria-sort': active ? (sortDir === 1 ? 'ascending' : 'descending') : 'none',
      onClick: () => sortBy(key),
      onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sortBy(key); } },
    }, label, el('span', { class: 'th-arrow' }, active ? (sortDir === 1 ? ' ▲' : ' ▼') : ''));
  }

  function table() {
    return el('div', { class: 'table-wrap' },
      el('table', { class: 'table' },
        el('thead', {},
          el('tr', {},
            el('th', { class: 'th-pick' },
              el('input', {
                type: 'checkbox',
                'aria-label': T.selectAllBooks,
                checked: visible().length > 0 && pickedVisible().length === visible().length,
                onChange: (e) => {
                  // Only what is on screen: "select all" under a search or a
                  // filter means all of these, not all 109.
                  const shown = visible();
                  if (e.target.checked) shown.forEach((r) => picked.add(r.title_id));
                  else shown.forEach((r) => picked.delete(r.title_id));
                  render();
                },
              })),
            sortableTh('title', T.colTitle),
            sortableTh('author', T.colAuthor),
            sortableTh('category', T.colCategory),
            sortableTh('added', T.colAdded),
            sortableTh('available', T.colAvailability),
            sortableTh('borrower', T.colOnLoanTo),
            sortableTh('due', T.colDue),
          )),
        el('tbody', {}, ...sortedRows().flatMap(rowsFor)),
      ));
  }

  // One request for every multi-copy title on screen. Single-copy titles need
  // nothing extra: their aggregate row already IS their copy row.
  async function loadExpansions(list) {
    const ids = list.filter((r) => r.total_copies > 1).map((r) => r.title_id);
    const map = new Map();
    if (!ids.length) return map;
    let all = [];
    try { all = await copiesForTitles(ids); } catch { return map; }
    for (const c of all) {
      if (!map.has(c.title_id)) map.set(c.title_id, []);
      map.get(c.title_id).push(c);
    }
    return map;
  }

  // A title contributes either one self-contained row, or a heading row plus a
  // row per copy.
  function rowsFor(r) {
    const kids = expanded.get(r.title_id);
    if (!kids?.length) return [rowFor(r)];
    return [rowFor(r, true), ...kids.map((c) => copyTableRow(r, c))];
  }

  function copyTableRow(r, c) {
    const open = (e) => { e.stopPropagation(); openPanel(r.title_id); };
    return el('tr', {
      class: `row-clickable row-copy ${selected === r.title_id ? 'row-selected' : ''}`,
      tabindex: '0',
      onClick: open,
      onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); } },
    },
      // Copies are selected with their title, never on their own — a label run
      // is per book, and the title row above already carries the tick.
      el('td', { class: 'cell-pick' }, ''),
      el('td', { class: 'cell-copy-code' }, c.copy_code),
      el('td', { class: 'cell-muted' }, ''),
      // Category and Added belong to the title, printed on the heading row
      // above; the copy rows leave them blank rather than repeating them down
      // the group.
      el('td', { class: 'cell-muted' }, ''),
      el('td', { class: 'cell-muted' }, ''),
      el('td', {}, el('span', { class: statusPill(c) }, T.copyStatus[c.status] ?? c.status)),
      el('td', { class: 'cell-muted' }, c.borrower || '—'),
      el('td', {},
        c.due_at
          ? el('span', { class: c.is_overdue ? 'pill pill-danger' : 'pill' },
              c.is_overdue ? T.overdueSince(formatDate(c.due_at)) : formatDate(c.due_at))
          : el('span', { class: 'cell-muted' }, '—')),
    );
  }

  // `heading` = the copies are listed below, so the per-copy columns would
  // repeat one of them and read as the answer for the whole title.
  function rowFor(r, heading = false) {
    const out = r.on_loan_copies > 0;
    const overdue = r.overdue_copies > 0;
    return el('tr', {
      class: `row-clickable ${heading ? 'row-title-head' : ''} ${selected === r.title_id ? 'row-selected' : ''}`,
      tabindex: '0',
      onClick: () => openPanel(r.title_id),
      onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPanel(r.title_id); } },
    },
      el('td', { class: 'cell-pick' },
        el('input', {
          type: 'checkbox',
          checked: picked.has(r.title_id),
          'aria-label': T.selectBook(r.title),
          // Without this the row's own click handler opens the side panel on
          // every tick, which is not what ticking a box should do.
          onClick: (e) => e.stopPropagation(),
          onChange: (e) => {
            if (e.target.checked) picked.add(r.title_id); else picked.delete(r.title_id);
            render();
          },
        })),
      el('td', { class: 'cell-strong' }, r.title),
      el('td', { class: 'cell-muted' }, r.author || '—'),
      el('td', { class: 'cell-muted' }, r.category || '—'),
      el('td', { class: 'cell-muted' }, r.added_at ? formatDate(r.added_at) : '—'),
      el('td', {}, availability(r)),
      // Only meaningful when something is out; a dash beats an empty cell,
      // which reads as missing data rather than "nothing to say".
      el('td', { class: 'cell-muted' },
        heading ? '' : (out ? (r.first_borrower || '—') : '—')),
      el('td', {},
        heading
          ? el('span', { class: 'cell-muted' }, '')
          : out
            ? el('span', { class: overdue ? 'pill pill-danger' : 'pill' },
                overdue ? T.overdueSince(formatDate(r.soonest_due)) : formatDate(r.soonest_due))
            : el('span', { class: 'cell-muted' }, '—')),
    );
  }

  function availability(r) {
    const cls = r.available_copies === 0 ? 'pill pill-warn' : 'pill';
    return el('span', { class: cls }, T.availableOf(r.available_copies, r.total_copies));
  }

  // --- Side panel ---------------------------------------------------------
  function panel() {
    return el('div', { class: 'panel-scrim', onClick: (e) => { if (e.target.classList.contains('panel-scrim')) closePanel(); } },
      el('aside', { class: 'panel', role: 'dialog', 'aria-label': adding ? T.addBook : T.bookDetails },
        el('button', { class: 'panel-close', 'aria-label': T.close, onClick: closePanel }, '×'),
        adding ? addPanel() : titlePanel(),
      ));
  }

  function addPanel() {
    if (!addPanelNode) {
      addPanelNode = addBookPanel({ onAdded: () => { refreshRows(); } });
    }
    return addPanelNode;
  }

  // Refresh the table WITHOUT re-rendering — a full render would detach the
  // add panel mid-use and lose focus and the session list.
  async function refreshRows() {
    try {
      rows = await listCatalogue({ search });
      expanded = await loadExpansions(rows);
    } catch { /* the panel already reported any failure */ }
    const wrap = view.querySelector('.table-wrap');
    if (wrap && rows.length) wrap.replaceWith(table());
  }

  function titlePanel() {
    const r = rows.find((x) => x.title_id === selected);
    if (!r) return skeletonBlock(4);
    if (editing) return editPanel(r);

    return el('div', {},
      el('h2', { class: 'panel-title' }, r.title),
      el('p', { class: 'muted' }, [r.author, r.category].filter(Boolean).join(' · ') || '—'),
      // Open to FRONT DESK as well as admin. `titles` is bibliographic and not
      // branch-owned, and 0003 already allowed any staff member to update it —
      // the app was simply never offering the action, so a typo caught at the
      // desk had to go to someone else.
      el('div', { class: 'action-row' },
        el('button', { class: 'btn btn-sm', onClick: () => { editing = true; render(); } }, T.editBook),
      ),

      el('dl', { class: 'detail' },
        // Only shown when there is something to show: an empty "Published —"
        // on every second-hand book is noise, not information.
        (r.published_year || r.published_place) ? el('dt', {}, T.published) : null,
        (r.published_year || r.published_place)
          ? el('dd', {}, [r.published_year, r.published_place].filter(Boolean).join(', '))
          : null,
        r.added_at ? el('dt', {}, T.colAdded) : null,
        r.added_at ? el('dd', {}, formatDate(r.added_at)) : null,
        el('dt', {}, T.colAvailability), el('dd', {}, T.availableOf(r.available_copies, r.total_copies)),
        r.out_of_service_copies > 0 ? el('dt', {}, T.outOfService) : null,
        r.out_of_service_copies > 0 ? el('dd', {}, String(r.out_of_service_copies)) : null,
      ),

      el('div', { class: 'session-head card-section' },
        el('h3', { class: 'card-title' }, T.copiesHeading),
        // Labels were previously only printable for books added in the current
        // sitting, which made them unreachable for anything already catalogued
        // — including every book added before today.
        copies.length
          ? el('button', { class: 'btn btn-sm', onClick: () => printFor(copies) }, T.printLabels)
          : null,
      ),
      panelLoading
        ? skeletonBlock(3)
        : el('div', {}, ...copies.map(copyRow)),

      panelLoading ? null : historySection(),
    );
  }

  // A note about the physical object — "cover torn, still readable". Per copy,
  // because two copies of the same title are not in the same state.
  //
  // Shown as plain text until someone chooses to edit it: this is read far more
  // often than it is written, and a textarea on every copy row turns a status
  // list into a form.
  function conditionNote(c) {
    if (editingNote === c.copy_id) {
      const box = el('textarea', {
        class: 'text-input', rows: '2', maxlength: '200',
        'aria-label': T.conditionNote, placeholder: T.conditionNotePlaceholder,
      });
      box.value = c.condition_note ?? '';
      queueMicrotask(() => box.focus());

      const save = async () => {
        if (savingNote) return;
        savingNote = true; render();
        try {
          const saved = await updateCopyCondition(c.copy_id, box.value);
          // Patch in place; a reload would close the panel and lose the
          // reader's position in a long list of copies.
          c.condition_note = saved.condition_note;
          editingNote = null;
          pendingNoteToast = saved.condition_note ? T.noteSaved : T.noteRemoved;
        } catch (err) {
          flashNotice(view, err.message);
        } finally {
          savingNote = false; render();
          if (pendingNoteToast) { flashSaved(view, pendingNoteToast); pendingNoteToast = null; }
        }
      };

      return el('div', { class: 'copy-note-edit' },
        box,
        el('div', { class: 'action-row' },
          el('button', { class: 'btn btn-sm btn-primary', type: 'button',
            disabled: savingNote || null, onClick: save },
            savingNote ? T.saving : T.saveNote),
          el('button', { class: 'btn btn-sm', type: 'button',
            onClick: () => { editingNote = null; render(); } }, T.cancel),
        ));
    }

    return el('div', { class: 'copy-note' },
      c.condition_note
        ? el('span', { class: 'pill pill-warn' }, c.condition_note)
        : el('span', { class: 'cell-muted' }, T.noConditionNote),
      el('button', {
        class: 'btn btn-sm', type: 'button',
        onClick: () => { editingNote = c.copy_id; render(); },
      }, c.condition_note ? T.editNote : T.addNote),
    );
  }

  // Every loan this title has had, newest first. Deliberately shows the
  // borrower's name: this is ordinary library practice and the desk asked for
  // it. Nothing here crosses a branch — the view is branch-scoped (0013).
  function historySection() {
    return el('div', {},
      el('div', { class: 'session-head card-section' },
        el('h3', { class: 'card-title' }, T.borrowHistory),
        history.length ? el('span', { class: 'muted' }, T.timesBorrowed(history.length)) : null,
      ),
      historyFailed
        ? el('p', { class: 'muted' }, T.historyUnavailable)
        : history.length
          ? el('div', {}, ...history.map(historyRow))
          : el('p', { class: 'empty' }, T.noBorrowHistory),
    );
  }

  function historyRow(l) {
    const out = !l.returned_at;
    return el('div', { class: 'copy-row' },
      el('div', { class: 'copy-head' },
        el('span', { class: 'cell-strong' },
          l.member_id
            ? el('a', { href: `#/members/${l.member_id}` }, l.borrower || T.unknownBorrower)
            : (l.borrower || T.unknownBorrower)),
        el('span', { class: 'basket-code' }, l.copy_code),
      ),
      el('dl', { class: 'detail' },
        el('dt', {}, T.issuedOn), el('dd', {}, formatDate(l.issued_at)),
        el('dt', {}, out ? T.dueBack : T.returnedOn),
        el('dd', {}, out
          ? el('span', { class: l.is_overdue ? 'pill pill-danger' : 'pill' }, formatDate(l.due_at))
          : formatDate(l.returned_at)),
        l.renewal_count ? el('dt', {}, T.renewals) : null,
        l.renewal_count ? el('dd', {}, String(l.renewal_count)) : null,
        // Who handled it. Blank for loans issued before 0009 recorded staff.
        l.issued_by_name ? el('dt', {}, T.issuedByLabel) : null,
        l.issued_by_name ? el('dd', {}, l.issued_by_name) : null,
        l.returned_by_name ? el('dt', {}, T.returnedByLabel) : null,
        l.returned_by_name ? el('dd', {}, l.returned_by_name) : null,
      ),
    );
  }

  // Title, author and category only. Copies are branch-owned physical objects
  // with codes already printed on labels — they are not editable here, and
  // nothing in this form can change one.
  function editPanel(r) {
    const title = el('input', {
      type: 'text', class: 'text-input', value: r.title, required: true,
      'aria-label': T.bookTitle, autocomplete: 'off',
    });
    const author = el('input', {
      type: 'text', class: 'text-input', value: r.author || '',
      'aria-label': T.author, autocomplete: 'off',
    });
    const publishedYear = el('input', {
      type: 'number', class: 'text-input', min: '1400', max: '2100', step: '1',
      inputmode: 'numeric', value: r.published_year ?? '',
      'aria-label': T.publishedYear, placeholder: T.publishedYearOptional,
    });
    const publishedPlace = el('input', {
      type: 'text', class: 'text-input', value: r.published_place ?? '',
      'aria-label': T.publishedPlace, placeholder: T.publishedPlaceOptional,
      autocomplete: 'off',
    });
    const known = categories.map((c) => c.name);
    if (r.category && !known.includes(r.category)) known.unshift(r.category);
    const category = el('select', { class: 'text-input', 'aria-label': T.category },
      el('option', { value: '' }, T.categoryNone),
      ...known.map((name) =>
        el('option', { value: name, selected: name === r.category }, name)));

    async function save(e) {
      e.preventDefault();
      if (savingEdit) return;
      savingEdit = true; render();
      try {
        const year = String(publishedYear.value || '').trim();
        const n = year ? parseInt(year, 10) : null;
        const saved = await updateTitle(r.title_id, {
          title: title.value,
          author: author.value.trim() || null,
          category: category.value || null,
          publishedYear: (n && n >= 1400 && n <= 2100) ? n : null,
          publishedPlace: publishedPlace.value.trim() || null,
        });
        // Patch the row in place: a full reload would close the panel and lose
        // the reader's place in a long catalogue.
        Object.assign(r, {
          title: saved.title, author: saved.author, category: saved.category,
          published_year: saved.published_year ?? null,
          published_place: saved.published_place ?? null,
        });
        savingEdit = false; editing = false;
        render();
        flashSaved(view, T.bookSaved);
      } catch (err) {
        savingEdit = false; render();
        flashNotice(view, err.message);
      }
    }

    return el('form', { onSubmit: save },
      el('h2', { class: 'panel-title' }, T.editBook),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.bookTitle), title),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.authorOptional), author),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.category), category),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.publishedYear), publishedYear),
      el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.publishedPlace), publishedPlace),
      el('div', { class: 'form-actions' },
        el('button', { class: 'btn btn-primary', type: 'submit', disabled: savingEdit },
          savingEdit ? T.saving : T.saveBookChanges),
        el('button', { class: 'btn', onClick: () => { editing = false; render(); } }, T.cancel),
      ),
    );
  }

  async function printFor(list) {
    const codes = list
      .filter((c) => c.copy_code)
      .map((c) => ({ code: c.copy_code, title: c.title }));
    if (!codes.length) return;
    const { openLabelSheet } = await import('../lib/labels.js');
    openLabelSheet(codes);
  }

  function copyRow(c) {
    return el('div', { class: 'copy-row' },
      el('div', { class: 'copy-head' },
        el('span', { class: 'basket-code' }, c.copy_code),
        el('span', { class: statusPill(c) }, T.copyStatus[c.status] ?? c.status),
      ),
      conditionNote(c),
      c.borrower
        ? el('dl', { class: 'detail' },
            el('dt', {}, T.borrowedBy),
            el('dd', {}, el('a', { href: `#/members/${c.member_id}` }, c.borrower)),
            el('dt', {}, T.dueBack),
            el('dd', {}, formatDate(c.due_at),
              c.is_overdue ? el('span', { class: 'pill pill-danger' }, T.daysOverdue(c.days_overdue)) : null),
            c.guardian_phone ? el('dt', {}, T.guardianPhone) : null,
            c.guardian_phone
              ? el('dd', {}, el('a', { href: `tel:${c.guardian_phone.replace(/\s/g, '')}` }, c.guardian_phone))
              : null,
          )
        : null,
    );
  }

  function statusPill(c) {
    if (c.is_overdue) return 'pill pill-danger';
    if (c.status === 'available') return 'pill pill-ok';
    if (c.status === 'on_loan') return 'pill';
    return 'pill pill-warn';
  }

  load();
  return { destroy: () => live.stop() };
}
